/**
 * Cálculo de condições de pagamento (desconto <-> total).
 * Extraído para uso direto nos componentes (não depender só do mixin).
 *
 * A taxa em % precisa de casas suficientes para, ao voltar pela fórmula
 * direta, o total arredondado em centavos ser o valor digitado.
 * Duas casas deixam resto (ex.: 20,21% de 1.880 = 1.500,05).
 */
export var PERCENT_DECIMALS = 8;
var PERCENT_STEP = 1 / Math.pow(10, PERCENT_DECIMALS);

export function calcDiscountValue(discount, value, increase, divideIncrease) {
    if (typeof increase === 'undefined') {
        increase = 0;
    }
    if (typeof divideIncrease === 'undefined') {
        divideIncrease = false;
    }

    var total = value;
    var installmentValue = value;

    if (discount.value || discount.value === 0) {
        total = discount.value.toString().split('+').reduce(function (accumulator, discValue) {
            discValue = parseFloat(discValue);
            return accumulator - (discount.type === 'percent' ? (accumulator * discValue / 100) : discValue);
        }, value);

        installmentValue = total / discount.installments;

        total += increase;
        installmentValue += divideIncrease ? (increase / discount.installments) : 0;
    }

    return Object.assign({}, discount, { total: total, installmentValue: installmentValue });
}

function formatPercent(percent) {
    var text = parseFloat(percent).toFixed(PERCENT_DECIMALS);
    text = text.replace(/0+$/, '').replace(/\.$/, '');
    if (text === '-0') {
        text = '0';
    }
    return text;
}

function centsText(value) {
    return parseFloat(value).toFixed(2);
}

function paymentTotalForPercent(base, increase, divideIncrease, installments, percentText) {
    return calcDiscountValue({
        type: 'percent',
        value: percentText,
        installments: installments || 1
    }, base, increase, divideIncrease).total;
}

/**
 * Taxa cuja aplicação, arredondada em centavos, reproduz o total desejado.
 * Retorna o texto que deve ser gravado em discount.value.
 */
function percentTextForDesiredTotal(base, increase, divideIncrease, installments, desiredTotal) {
    var exact = (base + increase - desiredTotal) / base * 100;
    var percent = parseFloat(exact.toFixed(PERCENT_DECIMALS));
    var desiredCents = centsText(desiredTotal);
    var bestText = formatPercent(percent);
    var bestDistance = Infinity;

    for (var attempt = 0; attempt < 6; attempt++) {
        var text = formatPercent(percent);
        var total = paymentTotalForPercent(base, increase, divideIncrease, installments, text);
        var distance = Math.abs(parseFloat(total) - parseFloat(desiredTotal));
        if (distance < bestDistance) {
            bestDistance = distance;
            bestText = text;
        }
        if (centsText(total) === desiredCents) {
            return text;
        }
        var direction = parseFloat(total) > parseFloat(desiredTotal) ? 1 : -1;
        var next = parseFloat((percent + direction * PERCENT_STEP).toFixed(PERCENT_DECIMALS));
        if (next === percent) {
            break;
        }
        percent = next;
    }

    return bestText;
}

/**
 * Caminho inverso: a partir do Total desejado, força type=value e calcula o desconto em R$.
 * Mutates `discount` (type, value, total, installmentValue).
 */
export function applyTotalToDiscount(discount, base, desiredTotal, increase, divideIncrease, limitMinValue) {
    if (typeof increase === 'undefined') {
        increase = 0;
    }
    if (typeof divideIncrease === 'undefined') {
        divideIncrease = false;
    }
    if (typeof limitMinValue === 'undefined') {
        limitMinValue = null;
    }

    base = parseFloat(base) || 0;
    increase = parseFloat(increase) || 0;
    var desired = parseFloat(desiredTotal);
    if (isNaN(desired)) {
        desired = base + increase;
    }

    // total exibido = baseDescontada + increase  =>  value(R$) = base + increase - desired
    var discountValue = parseFloat((base + increase - desired).toFixed(2));
    var clamped = false;
    var maxDiscount = null;

    if (
        limitMinValue
        && limitMinValue.discountAmountAvailable != null
        && discountValue > parseFloat(Number(limitMinValue.discountAmountAvailable).toFixed(2))
    ) {
        maxDiscount = parseFloat(Number(limitMinValue.discountAmountAvailable).toFixed(2));
        discountValue = maxDiscount;
        clamped = true;
    }

    discount.type = 'value';
    discount.value = discountValue;

    var calculated = calcDiscountValue(discount, base, increase, divideIncrease);
    discount.total = calculated.total;
    discount.installmentValue = calculated.installmentValue;

    return {
        discount: calculated,
        clamped: clamped,
        maxDiscount: maxDiscount
    };
}

/**
 * Converte o desconto entre % e R$ preservando o Total atual.
 * Mutates `discount` (type, value, total, installmentValue).
 * `discount.type` deve ser o tipo ANTERIOR (antes da troca).
 */
export function convertDiscountType(discount, newType, base, increase, divideIncrease, limitMinValue) {
    if (typeof increase === 'undefined') {
        increase = 0;
    }
    if (typeof divideIncrease === 'undefined') {
        divideIncrease = false;
    }
    if (typeof limitMinValue === 'undefined') {
        limitMinValue = null;
    }

    newType = newType === 'percent' ? 'percent' : 'value';
    var oldType = discount.type === 'percent' ? 'percent' : 'value';

    if (oldType === newType) {
        return {
            discount: calcDiscountValue(discount, base, increase, divideIncrease),
            clamped: false,
            maxDiscount: null
        };
    }

    base = parseFloat(base) || 0;
    increase = parseFloat(increase) || 0;

    var previous = Object.assign({}, discount, { type: oldType });
    var desiredTotal = calcDiscountValue(previous, base, increase, divideIncrease).total;

    if (!base) {
        discount.type = newType;
        discount.value = newType === 'percent' ? '0' : 0;
        var calculatedZero = calcDiscountValue(discount, base, increase, divideIncrease);
        discount.total = calculatedZero.total;
        discount.installmentValue = calculatedZero.installmentValue;
        return {
            discount: calculatedZero,
            clamped: false,
            maxDiscount: null
        };
    }

    if (newType === 'value') {
        return applyTotalToDiscount(discount, base, desiredTotal, increase, divideIncrease, limitMinValue);
    }

    var percentText = percentTextForDesiredTotal(
        base,
        increase,
        divideIncrease,
        discount.installments,
        desiredTotal
    );
    var percent = parseFloat(percentText);
    var clamped = false;
    var maxDiscount = null;

    if (limitMinValue && limitMinValue.discountPercentageMax != null) {
        var maxPercent = parseFloat(limitMinValue.discountPercentageMax);
        if (!isNaN(maxPercent) && percent > maxPercent) {
            percent = maxPercent;
            percentText = maxPercent + '';
            clamped = true;
            maxDiscount = maxPercent;
        }
    }

    discount.type = 'percent';
    discount.value = percentText;

    var calculated = calcDiscountValue(discount, base, increase, divideIncrease);
    discount.total = calculated.total;
    discount.installmentValue = calculated.installmentValue;

    return {
        discount: calculated,
        clamped: clamped,
        maxDiscount: maxDiscount,
        clampedAsPercent: true
    };
}
